import { useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  Divider,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  FormControlLabel,
  IconButton,
  Alert,
  Snackbar,
  Tooltip,
} from "@mui/material";
import {
  AccountBalance,
  Edit,
  CheckCircle,
  Cancel,
  Pending,
  Save,
  Close,
  PhoneAndroid,
  Verified,
  WarningAmber,
  CalendarMonth,
} from "@mui/icons-material";
import apiClient from "../utils/apiClient";
import { toast } from "react-toastify";

// ─── Helpers ─────────────────────────────────────────────────────────────────
const fmtDate = (d) =>
  d
    ? new Date(d).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })
    : "—";

const statusConfig = {
  approved: { color: "success", icon: <CheckCircle sx={{ fontSize: 15 }} />, label: "Approved" },
  rejected: { color: "error", icon: <Cancel sx={{ fontSize: 15 }} />, label: "Rejected" },
  pending: { color: "warning", icon: <Pending sx={{ fontSize: 15 }} />, label: "Pending" },
};

const StatusChip = ({ status }) => {
  const s = statusConfig[status] ?? { color: "default", icon: null, label: status ?? "N/A" };
  return (
    <Chip
      size="small"
      color={s.color}
      icon={s.icon}
      label={s.label}
      sx={{ fontWeight: 700, fontSize: 11, letterSpacing: 0.4 }}
    />
  );
};

const DetailRow = ({ label, value, mono = false, last = false }) => (
  <>
    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: 1.1 }}>
      <Typography
        variant="caption"
        sx={{ color: "text.secondary", fontWeight: 600, minWidth: 160, fontSize: 12 }}
      >
        {label}
      </Typography>
      <Box sx={{ textAlign: "right" }}>
        {typeof value === "string" || typeof value === "number" ? (
          <Typography
            variant="body2"
            sx={{ fontFamily: mono ? "monospace" : "inherit", fontWeight: 500, fontSize: 13 }}
          >
            {value || "—"}
          </Typography>
        ) : (
          value ?? <Typography variant="body2" color="text.disabled">—</Typography>
        )}
      </Box>
    </Box>
    {!last && <Divider />}
  </>
);

// ─── Main Component ───────────────────────────────────────────────────────────
export default function VendorPaymentDetails({ data, fetchVendor, id }) {
  const p = data;

  const [editOpen, setEditOpen] = useState(false);
  const [form, setForm] = useState({
    approval_status: p.approval_status ?? "pending",
    rejection_reason: p.rejection_reason ?? "",
    // isVerified: p.isVerified ?? false,
  });
  const [snack, setSnack] = useState({ open: false, msg: "", severity: "success" });

  const handleIsVerifyChange = async (newStatus) => {
    try {
      await apiClient.put(`/api/admin/vendor/payout/${id}`, { ...form });
      toast.success("Payout details updated successfully");
      fetchVendor();
      setEditOpen(false);
    } catch {
      toast.error("Failed to update verify status");
      setEditOpen(false);
    }
  };

  const handleSave = () => {
    handleIsVerifyChange()
  };

  return (
    <Box sx={{ mx: "auto", p: { xs: 2, md: 3 } }}>
      {/* ── Header ── */}
      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", mb: 3, flexWrap: "wrap", gap: 2 }}>
        <Box>
          <Typography variant="h6" fontWeight={800} letterSpacing={-0.4}>
            Vendor Payment Details
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Payout ID: <span style={{ fontFamily: "monospace" }}>{p.payoutId}</span>
          </Typography>
        </Box>
        <Button
          variant="contained"
          size="small"
          startIcon={<Edit fontSize="small" />}
          onClick={() => setEditOpen(true)}
          sx={{ textTransform: "none", fontWeight: 700, borderRadius: 2 }}
        >
          Update Status
        </Button>
      </Box>

      <Grid container spacing={2.5}>
        {/* ── Bank Details Card ── */}
        <Grid item size={{ xs: 12, md: 6 }}>
          <Card variant="outlined" sx={{ borderRadius: 3, height: "100%" }}>
            <CardContent sx={{ pb: "16px !important" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                <AccountBalance fontSize="small" color="primary" />
                <Typography variant="subtitle2" fontWeight={700}>
                  Bank Details
                </Typography>
                <Box sx={{ ml: "auto" }}>
                  <Chip
                    size="small"
                    variant="outlined"
                    label={p.payoutMethod?.replace("_", " ").toUpperCase() ?? "—"}
                    sx={{ fontSize: 10, fontWeight: 700 }}
                  />
                </Box>
              </Box>
              <Divider sx={{ mb: 0.5 }} />
              <DetailRow label="Account Holder" value={p.accountHolderName?.trim()} />
              <DetailRow label="Bank Name" value={p.bankName?.toUpperCase()} />
              <DetailRow label="Account Number" value={p.accountNumber} mono />
              <DetailRow label="IFSC Code" value={p.ifscCode} mono />
              <DetailRow label="Branch Name" value={p.branchName} last />
            </CardContent>
          </Card>
        </Grid>

        {/* ── UPI & Approval Status Card ── */}
        <Grid item size={{ xs: 12, md: 6 }}>
          <Card variant="outlined" sx={{ borderRadius: 3, height: "100%" }}>
            <CardContent sx={{ pb: "16px !important" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                <PhoneAndroid fontSize="small" color="secondary" />
                <Typography variant="subtitle2" fontWeight={700}>
                  UPI & Status
                </Typography>
              </Box>
              <Divider sx={{ mb: 0.5 }} />
              <DetailRow label="UPI ID" value={p.upiId} mono />
              <DetailRow
                label="Approval Status"
                value={<StatusChip status={p.approval_status} />}
              />
              <DetailRow
                label="Payout Verified"
                value={
                  <Chip
                    size="small"
                    color={p.isVerified ? "success" : "warning"}
                    icon={
                      p.isVerified
                        ? <Verified sx={{ fontSize: 13 }} />
                        : <WarningAmber sx={{ fontSize: 13 }} />
                    }
                    label={p.isVerified ? "Verified" : "Unverified"}
                    sx={{ fontWeight: 700, fontSize: 11 }}
                  />
                }
              />
              <DetailRow
                label="Submitted"
                value={
                  <Chip
                    size="small"
                    color={p.is_submitted ? "info" : "default"}
                    label={p.is_submitted ? "Yes" : "No"}
                    sx={{ fontWeight: 700, fontSize: 11 }}
                  />
                }
              />
              {/* <DetailRow label="Approved By" value={p.approvedBy} last /> */}
            </CardContent>
          </Card>
        </Grid>

        {/* ── Timeline Card ── */}
        <Grid item size={{ xs: 12 }}>
          <Card variant="outlined" sx={{ borderRadius: 3 }}>
            <CardContent sx={{ pb: "16px !important" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                <CalendarMonth fontSize="small" color="action" />
                <Typography variant="subtitle2" fontWeight={700}>
                  Timeline
                </Typography>
              </Box>
              <Divider sx={{ mb: 0.5 }} />
              <Grid container>
                <Grid item size={{ xs: 12, sm: 6 }}>
                  <DetailRow label="Created At" value={fmtDate(p.createdAt)} />
                  <DetailRow label="Submitted At" value={fmtDate(p.submitted_at)} />
                  {/* <DetailRow label="Updated At" value={fmtDate(p.updatedAt)} last /> */}
                </Grid>
                <Grid item size={{ xs: 12, sm: 6 }} sx={{ borderLeft: { sm: "1px solid" }, borderColor: { sm: "divider" }, pl: { sm: 2 } }}>
                  <DetailRow label="Approved At" value={fmtDate(p.approved_at)} />
                  <DetailRow label="Rejected At" value={fmtDate(p.rejected_at)} />
                  {/* <DetailRow label="Deleted At" value={fmtDate(p.deletedAt)} last /> */}
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* ── Rejection Reason ── */}
        {p.approval_status === "rejected" && p.rejection_reason && (
          <Grid item size={{ xs: 12 }}>
            <Alert
              severity="error"
              icon={<Cancel fontSize="small" />}
              sx={{ borderRadius: 3 }}
            >
              <Typography variant="caption" fontWeight={700} display="block">
                Rejection Reason
              </Typography>
              <Typography variant="body2">{p.rejection_reason}</Typography>
            </Alert>
          </Grid>
        )}
      </Grid>

      {/* ── Edit Dialog ── */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pb: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Edit fontSize="small" />
            <Typography fontWeight={700} fontSize={15}>Update Payout Status</Typography>
          </Box>
          <IconButton size="small" onClick={() => setEditOpen(false)}>
            <Close fontSize="small" />
          </IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent dividers sx={{ pt: 2.5, display: "flex", flexDirection: "column", gap: 2.5 }}>
          {/* Approval Status */}
          <FormControl fullWidth size="small">
            <InputLabel>Approval Status</InputLabel>
            <Select
              value={form.approval_status}
              label="Approval Status"
              onChange={(e) => setForm({ ...form, approval_status: e.target.value })}
            >
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="approved">Approved</MenuItem>
              <MenuItem value="rejected">Rejected</MenuItem>
            </Select>
          </FormControl>

          {/* Rejection Reason – only when rejected */}
          {form.approval_status === "rejected" && (
            <TextField
              fullWidth
              size="small"
              label="Rejection Reason"
              multiline
              rows={3}
              value={form.rejection_reason}
              onChange={(e) => setForm({ ...form, rejection_reason: e.target.value })}
              placeholder="Enter reason for rejection..."
            />
          )}

          {/* isVerified toggle */}
          {/* <FormControlLabel
            control={
              <Switch
                checked={form.isVerified}
                onChange={(e) => setForm({ ...form, isVerified: e.target.checked })}
                color="success"
              />
            }
            label={
              <Typography variant="body2" fontWeight={600}>
                Mark Payout as Verified
              </Typography>
            }
          /> */}
        </DialogContent>
        <Divider />
        <DialogActions sx={{ px: 2.5, py: 1.5, gap: 1 }}>
          <Button size="small" variant="outlined" onClick={() => setEditOpen(false)}>
            Cancel
          </Button>
          <Button size="small" variant="contained" startIcon={<Save />} onClick={() => handleSave()}>
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Snackbar ── */}
      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        onClose={() => setSnack({ ...snack, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert severity={snack.severity} onClose={() => setSnack({ ...snack, open: false })} sx={{ borderRadius: 2 }}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}